/**
 * Global time range: presets, pin-to-now, explicit from/to inputs and the
 * `brush` slot, which the top bar fills with the miniature price line
 * (components/MiniBrush). The slot disappears entirely when nothing is
 * passed, rather than showing an empty box.
 *
 * Nothing here wraps inside a control; optional pieces are dropped by the
 * top bar's container width instead (brush below 1360px, nudge/zoom below
 * 1200px, the range text below 1000px, where a ✎ button still opens the
 * editor).
 */
import { useState, type ReactNode } from "react";
import { fmtTime } from "@/lib/format";
import { PRESETS, type Preset } from "@/lib/time";
import { useUrlState } from "@/state/useUrlState";

export function TimeRangePicker({ brush }: { brush?: ReactNode }) {
  const { state, range, setPreset, setRange, setPinned, nudge, zoom } = useUrlState();
  const [editing, setEditing] = useState(false);
  const activePreset: Preset | undefined = state.range.preset;
  const rangeText = `${fmtTime(range.from, "MM-dd HH:mm")} → ${range.pinned ? "now" : fmtTime(range.to, "MM-dd HH:mm")}`;
  return (
    <div className="flex shrink-0 items-center gap-1 whitespace-nowrap">
      <div className="flex shrink-0" role="group" aria-label="time range presets">
        {PRESETS.map((p) => (
          <button
            key={p}
            type="button"
            className={`ctl shrink-0 rounded-none px-1 @min-[1000px]:px-1.5 first:rounded-l-sm last:rounded-r-sm ${activePreset === p && !state.range.from ? "on" : ""}`}
            onClick={() => setPreset(p)}
          >
            {p}
          </button>
        ))}
      </div>
      <div className="hidden shrink-0 items-center gap-1 @min-[1200px]:flex">
        <button type="button" className="ctl" title="nudge earlier [" onClick={() => nudge(-0.5)}>
          ‹
        </button>
        <button type="button" className="ctl" title="nudge later ]" onClick={() => nudge(0.5)}>
          ›
        </button>
        <button type="button" className="ctl" title="zoom in +" onClick={() => zoom(0.5)}>
          +
        </button>
        <button type="button" className="ctl" title="zoom out -" onClick={() => zoom(2)}>
          −
        </button>
      </div>
      <button
        type="button"
        className={`ctl shrink-0 ${range.pinned ? "on" : ""}`}
        aria-pressed={range.pinned}
        title={range.pinned ? "pinned to now (click to freeze)" : "frozen (click to pin to now)"}
        onClick={() => setPinned(!range.pinned)}
      >
        {range.pinned ? "⌖ now" : "⏸ fixed"}
      </button>
      <button
        type="button"
        className="ctl num hidden shrink-0 text-[11px] @min-[1000px]:block"
        onClick={() => setEditing((v) => !v)}
        title={`edit range (${rangeText})`}
      >
        {rangeText}
      </button>
      <button
        type="button"
        className="ctl shrink-0 @min-[1000px]:hidden"
        onClick={() => setEditing((v) => !v)}
        title={`edit range (${rangeText})`}
        aria-label="edit range"
      >
        ✎
      </button>
      {editing && (
        <RangeEditor
          from={range.from}
          to={range.to}
          onApply={(f, t) => {
            setRange(f, t);
            setEditing(false);
          }}
          onCancel={() => setEditing(false)}
        />
      )}
      {brush && (
        <div className="ml-1 hidden shrink-0 @min-[1360px]:block" title="drag to set the time range">
          {brush}
        </div>
      )}
    </div>
  );
}

function toLocalInput(t: number): string {
  const d = new Date(t * 1000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function RangeEditor({
  from,
  to,
  onApply,
  onCancel,
}: {
  from: number;
  to: number;
  onApply: (from: number, to: number) => void;
  onCancel: () => void;
}) {
  const [f, setF] = useState(toLocalInput(from));
  const [t, setT] = useState(toLocalInput(to));
  return (
    <form
      className="absolute top-full left-1/2 z-30 mt-px flex max-w-[calc(100vw-1rem)] -translate-x-1/2 flex-wrap items-center justify-center gap-1 rounded-sm border border-border bg-bg-elev p-2 shadow-lg"
      onSubmit={(e) => {
        e.preventDefault();
        const a = Math.floor(new Date(f).getTime() / 1000);
        const b = Math.floor(new Date(t).getTime() / 1000);
        if (Number.isFinite(a) && Number.isFinite(b) && a < b) onApply(a, b);
      }}
    >
      <input className="ctl" type="datetime-local" value={f} onChange={(e) => setF(e.target.value)} />
      <span className="text-fg-muted">→</span>
      <input className="ctl" type="datetime-local" value={t} onChange={(e) => setT(e.target.value)} />
      <button type="submit" className="ctl on">
        apply
      </button>
      <button type="button" className="ctl" onClick={onCancel}>
        cancel
      </button>
    </form>
  );
}
